import { computeSlots } from '../pages/MealPlans/mealPlanUtils';
import { formatSubscriptionPlanLabel } from '../pages/MealPlans/subscriptionDayBands';

/** Weekday columns for meal-slot × delivery-pattern tables. */
export const WEEKDAY_COLUMNS = [
  { key: 'Monday', short: 'Mon' },
  { key: 'Tuesday', short: 'Tue' },
  { key: 'Wednesday', short: 'Wed' },
  { key: 'Thursday', short: 'Thu' },
  { key: 'Friday', short: 'Fri' },
  { key: 'Saturday', short: 'Sat' },
  { key: 'Sunday', short: 'Sun' },
];

const DAY_ALIASES = {
  mon: 'Monday',
  monday: 'Monday',
  tue: 'Tuesday',
  tues: 'Tuesday',
  tuesday: 'Tuesday',
  wed: 'Wednesday',
  wednesday: 'Wednesday',
  thu: 'Thursday',
  thur: 'Thursday',
  thurs: 'Thursday',
  thursday: 'Thursday',
  fri: 'Friday',
  friday: 'Friday',
  sat: 'Saturday',
  saturday: 'Saturday',
  sun: 'Sunday',
  sunday: 'Sunday',
  0: 'Sunday',
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
};

const SLOT_SUFFIX_RE =
  /\s*[-–—]\s*(Breakfast|Lunch|Dinner|Snacks?|Morning|Evening|Brunch)\s*$/i;

const SLOT_LABEL_BY_ID = {
  breakfast: 'Breakfast',
  mid_morning: 'Mid-morning',
  lunch: 'Lunch',
  evening_snack: 'Evening snack',
  dinner: 'Dinner',
  full_day: 'Full Day',
};

const SLOT_PATTERN_ORDER = [
  'breakfast',
  'mid_morning',
  'lunch',
  'evening_snack',
  'dinner',
];

function normalizeDayName(raw) {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return DAY_ALIASES[String(raw)] || null;
  }
  const s = String(raw).trim();
  if (!s) return null;
  return DAY_ALIASES[s.toLowerCase()] || (WEEKDAY_COLUMNS.some((d) => d.key === s) ? s : null);
}

export function normalizeDayList(days) {
  if (!Array.isArray(days)) return [];
  const out = [];
  for (const d of days) {
    const name = normalizeDayName(d);
    if (name && !out.includes(name)) out.push(name);
  }
  return out;
}

export function toIsoDateOnly(value) {
  if (value == null || value === '') return '';
  const raw = String(value).trim();
  const match = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (match) return match[1];
  return raw;
}

export function resolveSubscriptionDateRange(source = {}, snapshot = {}) {
  const startIso = toIsoDateOnly(
    snapshot.startDate
    || snapshot.fromDate
    || source.startDate
    || source.fromDate,
  );
  const endIso = toIsoDateOnly(
    snapshot.endDate
    || snapshot.toDate
    || source.endDate
    || source.toDate,
  );
  return { startIso, endIso };
}

export function filterItemSchedulesForRange(itemSchedules = [], startIso = '', endIso = '') {
  if (!Array.isArray(itemSchedules)) return [];
  return itemSchedules.map((row) => {
    if (!row || typeof row !== 'object') return row;
    return {
      ...row,
      days: weekdaysInSubscriptionRange(startIso, endIso, row.days || []),
    };
  });
}

export function formatDayPattern(days = []) {
  if (!days.length) return '—';
  const order = WEEKDAY_COLUMNS.map((d) => d.key);
  return [...days]
    .sort((a, b) => order.indexOf(a) - order.indexOf(b))
    .map((d) => {
      const col = WEEKDAY_COLUMNS.find((c) => c.key === d);
      return col?.short || d.slice(0, 3);
    })
    .join(', ');
}

/** Weekdays from a schedule that actually fall inside the subscription date range. */
export function weekdaysInSubscriptionRange(
  startIso,
  endIso,
  scheduleDays = [],
  fallbackDays = [],
) {
  const normalized = normalizeDayList(scheduleDays);
  if (!normalized.length) return [];
  const start = toIsoDateOnly(startIso);
  const end = toIsoDateOnly(endIso);
  if (!start || !end) {
    const fallback = normalizeDayList(fallbackDays);
    return fallback.length ? fallback : [];
  }
  const slots = computeSlots({ startDate: start, endDate: end, days: normalized });
  const occurred = new Set(slots.map((slot) => slot.dayName).filter(Boolean));
  return WEEKDAY_COLUMNS.map((day) => day.key).filter((day) => occurred.has(day));
}

/** Union of weekdays with at least one delivery across item schedules in range. */
export function collectActiveDeliveryWeekdays({
  startDate,
  endDate,
  itemScheduleRows = [],
} = {}) {
  const allDays = new Set();
  (itemScheduleRows || []).forEach((row) => {
    weekdaysInSubscriptionRange(startDate, endDate, row?.days).forEach((day) => {
      allDays.add(day);
    });
  });
  return WEEKDAY_COLUMNS.map((day) => day.key).filter((day) => allDays.has(day));
}

function slotDisplayLabel(code) {
  const key = String(code || '').trim().toLowerCase();
  if (!key) return '';
  if (key === 'full_day') return SLOT_LABEL_BY_ID.full_day;
  return SLOT_LABEL_BY_ID[key] || humanizeCode(key);
}

function sortSlotIds(slotIds = []) {
  return [...slotIds].sort((a, b) => {
    const ai = SLOT_PATTERN_ORDER.indexOf(a);
    const bi = SLOT_PATTERN_ORDER.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
}

function formatSlotProductName(product) {
  const name = String(product?.name || '').trim();
  if (!name) return undefined;
  const qty = Number(product?.quantity);
  if (Number.isFinite(qty) && qty > 1) return `${name} × ${qty}`;
  return name;
}

function findScheduledProduct(products = [], schedule, slotId) {
  const slotKey = slotCodeKey(slotId);
  const productId = schedule?.productId;
  if (productId != null && String(productId) !== '') {
    const exact = products.find((item) => (
      String(item.id) === String(productId)
      && (!item.slotId || slotCodeKey(item.slotId) === slotKey)
    ));
    if (exact) return exact;
    const byId = products.find((item) => String(item.id) === String(productId));
    if (byId) return byId;
  }
  return null;
}

function countableSlotLines(itemSchedules = [], products = []) {
  const schedules = (itemSchedules || []).filter((row) => {
    const slotId = String(row?.slotId || '').trim().toLowerCase();
    return Boolean(slotId) && slotId !== 'full_day';
  });
  if (schedules.length) return schedules.length;
  return (products || []).filter((item) => {
    const slotId = slotCodeKey(item?.slotId || item?.slotCode || item?.slot);
    return Boolean(slotId) && slotId !== 'full_day';
  }).length;
}

function buildSlotPatternRowsFromItemSchedules({
  itemSchedules = [],
  products = [],
  startIso = '',
  endIso = '',
  planDays = [],
}) {
  const bySlot = new Map();
  itemSchedules.forEach((row) => {
    const slotId = String(row?.slotId || '').trim().toLowerCase();
    if (!slotId || slotId === 'full_day') return;
    if (!bySlot.has(slotId)) bySlot.set(slotId, []);
    bySlot.get(slotId).push(row);
  });

  const slotIds = sortSlotIds([...bySlot.keys()]);
  if (!slotIds.length) return [];

  const rows = [];
  slotIds.forEach((slotId) => {
    (bySlot.get(slotId) || []).forEach((schedule) => {
      let days = normalizeDayList(schedule?.days);
      days = weekdaysInSubscriptionRange(startIso, endIso, days, planDays);
      const product = findScheduledProduct(products, schedule, slotId);
      rows.push({
        slotLabel: slotDisplayLabel(slotId),
        slotCode: slotId,
        days,
        patternLabel: formatDayPattern(days),
        productName: formatSlotProductName(product)
          || (schedule?.productId != null ? String(schedule.productId) : undefined),
      });
    });
  });
  return rows;
}

function slotCodeKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
}

function isWeakSpecialtyLabel(value) {
  const s = String(value || '')
    .trim()
    .toLowerCase();
  // Empty / UI placeholders only — never treat a DB specialty name as invalid.
  return !s || s === '—' || s === '-' || s === 'n/a' || s === 'meal plan';
}

function humanizeCode(code) {
  return String(code || '')
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function looksLikeCodeAsName(name, code) {
  if (!name || !code) return false;
  return String(name).trim().toLowerCase() === String(code).trim().toLowerCase();
}

/**
 * Resolve snapshot object from either an API subscription row or a live builder payload.
 */
export function getSubscriptionSnapshot(source = {}) {
  if (source && typeof source.webPlanSnapshot === 'object' && source.webPlanSnapshot) {
    return source.webPlanSnapshot;
  }
  return source || {};
}

/** Display name when a subscription is built only from Our Menu dishes. */
export const OUR_MENU_SELECTION_LABEL = 'Our menu';

function productLooksPackaged(product) {
  if (!product || typeof product !== 'object') return false;
  if (product.planId || product.isFullDayPackage || product.isSpecialtyBundle) return true;
  const kind = String(product.kind || product.source || '');
  if (kind === 'specialty' || kind === 'full_day') return true;
  const category = String(product.category || '');
  return /specialty/i.test(category) || category === 'Full Day' || category === 'Plan Base';
}

function collectProducts(source, snapshot) {
  if (Array.isArray(snapshot?.products)) return snapshot.products;
  if (Array.isArray(source?.products)) return source.products;
  return [];
}

/**
 * True when every selected line is Regular Our menu (no specialty / Full Day).
 */
export function isOurMenuOnlySource(source = {}) {
  const snapshot = getSubscriptionSnapshot(source);
  const products = collectProducts(source, snapshot);
  if (products.some(productLooksPackaged)) return false;
  const storedName = String(snapshot.specialtyName || source.specialtyName || '').trim();
  if (storedName.toLowerCase() === OUR_MENU_SELECTION_LABEL.toLowerCase()) return true;
  if (!products.length) return false;
  return true;
}

/**
 * Specialty display name from API/snapshot (admin-configured), with product-name
 * override only when the stored specialty looks empty/placeholder or is just the code.
 */
export function resolveSpecialtyLabel(source = {}, fallback = '—') {
  if (isOurMenuOnlySource(source)) {
    return OUR_MENU_SELECTION_LABEL;
  }

  const snapshot = getSubscriptionSnapshot(source);
  const specialtyName = snapshot.specialtyName || source.specialtyName || null;
  const code = snapshot.specialtyCode || snapshot.specialtyPlanId || source.specialtyPlanId || null;
  const products = collectProducts(source, snapshot);

  const specialtyOnly = products
    .filter((p) => productLooksPackaged(p) || /specialty/i.test(String(p?.category || '')))
    .map((p) => String(p?.name || '').replace(SLOT_SUFFIX_RE, '').trim())
    .filter((n) => n && !isWeakSpecialtyLabel(n));
  const uniqueSpecialty = Array.from(new Set(specialtyOnly));
  const candidate = uniqueSpecialty[0];
  if (candidate) {
    const preferProducts = isWeakSpecialtyLabel(specialtyName)
      || (
        looksLikeCodeAsName(specialtyName, code)
        && candidate.toLowerCase() !== String(specialtyName).trim().toLowerCase()
      );
    if (preferProducts) return candidate;
  }

  if (specialtyName && !isWeakSpecialtyLabel(specialtyName)) {
    return String(specialtyName).trim();
  }
  if (specialtyName) return String(specialtyName).trim();
  if (code) return humanizeCode(code);
  return fallback;
}

/** Raw region key from snapshot/payload. */
export function resolveRegionKey(source = {}) {
  const snapshot = getSubscriptionSnapshot(source);
  const raw =
    snapshot.regionKey
    || snapshot.specialtyRegionKey
    || source.regionKey
    || source.specialtyRegionKey
    || null;
  if (raw == null || raw === '') return null;
  return String(raw).trim().toLowerCase();
}

/**
 * Canonical region display labels (keys stay south/north in DB/API).
 * Keep in sync with specialtyCatalogApi.REGION_LABELS / admin REGION_OPTIONS.
 */
export const REGION_DISPLAY_LABELS = {
  south: 'South Indian',
  north: 'North Indian',
  east: 'East Indian',
  west: 'West Indian',
};

/**
 * Canonical region display labels (keys stay south/north in DB/API).
 */
export function canonicalRegionLabel(rawKeyOrLabel) {
  if (rawKeyOrLabel == null || rawKeyOrLabel === '') return null;
  const raw = String(rawKeyOrLabel).trim();
  if (!raw) return null;
  const lower = raw.toLowerCase();
  const key = lower
    .replace(/\s+indian\s*$/i, '')
    .replace(/\s+meal\s*$/i, '')
    .trim();
  const fromKey = REGION_DISPLAY_LABELS[key];
  if (fromKey) return fromKey;
  const fromLower = REGION_DISPLAY_LABELS[lower];
  if (fromLower) return fromLower;
  // Already a full label like "South Indian"
  if (/\bindian\b/i.test(raw)) return raw.replace(/\s+Meal$/i, '').trim();
  return null;
}

/**
 * Region label from snapshot/API when stored; otherwise map region key.
 * Always prefers South Indian / North Indian for known keys.
 */
export function resolveRegionLabel(source = {}) {
  const snapshot = getSubscriptionSnapshot(source);
  const key = resolveRegionKey(source);
  if (key) {
    const fromKey = canonicalRegionLabel(key);
    if (fromKey) return fromKey;
  }
  const stored =
    snapshot.regionLabel
    || source.regionLabel
    || source.specialtyRegionLabel
    || null;
  const fromStored = canonicalRegionLabel(stored);
  if (fromStored) return fromStored;
  if (stored && String(stored).trim()) {
    return String(stored).trim().replace(/\s+Meal$/i, '');
  }
  if (key) return humanizeCode(key);
  return null;
}

/**
 * Specialty with region when present, e.g. "{specialtyName} · {regionLabel}".
 */
export function formatSpecialtyWithRegion(source = {}, fallback = '—') {
  const specialty = resolveSpecialtyLabel(source, fallback);
  if (isOurMenuOnlySource(source)) return specialty;
  const region = resolveRegionLabel(source);
  if (!specialty || specialty === '—') return region || fallback;
  if (!region) return specialty;
  return `${specialty} · ${region}`;
}

/**
 * Duration / billing plan name from snapshot (e.g. "2 MONTHS PLAN", "CUSTOM PLAN").
 */
export function resolvePlanLabel(source = {}, fallback = '—') {
  const snapshot = getSubscriptionSnapshot(source);
  const deliveryDayCount = Number(
    snapshot.deliveryDayCount
    ?? snapshot.charges?.days
    ?? source.deliveryDayCount
    ?? source.originalCount
    ?? 0,
  );
  if (deliveryDayCount > 0) {
    return formatSubscriptionPlanLabel(deliveryDayCount);
  }
  const label = snapshot.planLabel || source.planLabel || null;
  if (label && String(label).trim()) return String(label).trim();
  return fallback;
}

/**
 * Organic / Regular from snapshot flags. Returns null when unknown.
 */
export function resolveDietLabel(source = {}) {
  const snapshot = getSubscriptionSnapshot(source);
  const raw =
    snapshot.isOrganic
    ?? snapshot.specialtyOrganic
    ?? source.isOrganic
    ?? source.specialtyOrganic;
  if (raw == null || raw === '') return null;
  return raw ? 'Organic' : 'Regular';
}

function resolveDisplaySlots(source, snapshot) {
  const uiCodes = Array.isArray(snapshot.uiSlotCodes) && snapshot.uiSlotCodes.length
    ? snapshot.uiSlotCodes.map((c) => String(c)).filter(Boolean)
    : [];
  if (uiCodes.length) {
    return uiCodes.map((c) => slotDisplayLabel(c));
  }

  const fromSnapLabels = Array.isArray(snapshot.slotLabels)
    ? snapshot.slotLabels.map((s) => String(s)).filter(Boolean)
    : [];
  if (fromSnapLabels.length === 1
    && fromSnapLabels[0].toLowerCase() === 'breakfast'
    && snapshot.selectionMode === 'mixed_or_full_day') {
    const internalSlots = ['breakfast', 'mid_morning', 'lunch', 'evening_snack', 'dinner'];
    const codes = Array.isArray(snapshot.slotCodes)
      ? snapshot.slotCodes.map((c) => String(c).toLowerCase())
      : [];
    if (internalSlots.every((slot) => codes.includes(slot))) {
      return [SLOT_LABEL_BY_ID.full_day];
    }
  }
  if (fromSnapLabels.length) {
    return fromSnapLabels.map((label) => slotDisplayLabel(label));
  }

  const codes = Array.isArray(source.purchasedSlots) && source.purchasedSlots.length
    ? source.purchasedSlots
    : Array.isArray(snapshot.slotCodes) && snapshot.slotCodes.length
      ? snapshot.slotCodes
      : Array.isArray(snapshot.mealSlots)
        ? snapshot.mealSlots
        : [];
  if (codes.length) {
    return codes.map((c) => slotDisplayLabel(c));
  }

  if (snapshot.mealLabel) {
    return String(snapshot.mealLabel)
      .split(/[,|•]/)
      .map((part) => part.trim())
      .filter(Boolean);
  }

  if (source.slot) return [String(source.slot)];
  return [];
}

/** Meal slot labels for subscription lists (admin-style Specialty/Plan secondary line). */
export function formatSubscriptionMealSlots(source = {}, snapshot = {}) {
  const snap = snapshot && Object.keys(snapshot).length ? snapshot : getSubscriptionSnapshot(source);
  const slots = resolveDisplaySlots(source, snap);
  return slots.length ? slots.join(', ') : '—';
}

/**
 * Build one row per selected dish (slot × item) with weekday delivery pattern.
 * @returns {{ slotLabel: string, slotCode: string, days: string[], patternLabel: string, productName?: string }[]}
 */
export function buildSlotPatternRows(source = {}) {
  const snapshot = getSubscriptionSnapshot(source);
  const { startIso, endIso } = resolveSubscriptionDateRange(source, snapshot);
  const products = Array.isArray(snapshot.products) ? snapshot.products : [];
  const itemSchedules = Array.isArray(snapshot.itemSchedules)
    ? snapshot.itemSchedules
    : (Array.isArray(source.itemSchedules) ? source.itemSchedules : []);
  const slotSchedules = Array.isArray(snapshot.slotSchedules) ? snapshot.slotSchedules : [];
  const storedPatternRows = Array.isArray(snapshot.slotPatternRows) ? snapshot.slotPatternRows : [];
  const expectedLines = countableSlotLines(itemSchedules, products);
  if (storedPatternRows.length && storedPatternRows.length >= Math.max(1, expectedLines)) {
    return storedPatternRows;
  }

  const planDays = normalizeDayList(snapshot.days || source.days);

  const fromItemSchedules = buildSlotPatternRowsFromItemSchedules({
    itemSchedules,
    products,
    startIso,
    endIso,
    planDays: weekdaysInSubscriptionRange(startIso, endIso, planDays, planDays),
  });
  if (fromItemSchedules.length) return fromItemSchedules;

  const slotCodes = Array.isArray(snapshot.slotCodes)
    ? snapshot.slotCodes.map((s) => String(s))
    : Array.isArray(source.purchasedSlots)
      ? source.purchasedSlots.map((s) => String(s))
      : Array.isArray(snapshot.mealSlots)
        ? snapshot.mealSlots.map((s) => String(s))
        : [];

  const displaySlots = resolveDisplaySlots(source, snapshot);
  const filteredPlanDays = weekdaysInSubscriptionRange(startIso, endIso, planDays, planDays);

  const productsBySlot = new Map();
  products.forEach((product) => {
    const slotId = slotCodeKey(product?.slotId || product?.slotCode || product?.slot);
    if (!slotId || slotId === 'full_day') return;
    if (!productsBySlot.has(slotId)) productsBySlot.set(slotId, []);
    productsBySlot.get(slotId).push(product);
  });

  const count = Math.max(displaySlots.length, slotCodes.length);
  if (!count && !productsBySlot.size) return [];

  const rows = [];
  const seenSlots = new Set();
  const pushProductRows = (slotId, label, fallbackDays) => {
    const slotKey = slotCodeKey(slotId);
    seenSlots.add(slotKey);
    const slotProducts = productsBySlot.get(slotKey) || [];
    const slotSchedule = slotSchedules.find((s) => {
      const sid = slotCodeKey(s.slotId || s.slotCode || s.slot || s.slotLabel);
      return sid === slotKey || sid === slotCodeKey(label);
    });
    const items = slotProducts.length ? slotProducts : [null];
    items.forEach((product) => {
      let days = normalizeDayList(product?.days);
      if (!days.length) days = normalizeDayList(slotSchedule?.days);
      if (!days.length) days = fallbackDays;
      days = weekdaysInSubscriptionRange(startIso, endIso, days, filteredPlanDays);
      rows.push({
        slotLabel: label || slotDisplayLabel(slotId),
        slotCode: slotId,
        days,
        patternLabel: formatDayPattern(days),
        productName: formatSlotProductName(product),
      });
    });
  };

  for (let i = 0; i < count; i += 1) {
    const rawCode = String(slotCodes[i] || displaySlots[i] || `slot_${i + 1}`);
    if (rawCode === 'full_day') continue;
    const label = String(displaySlots[i] || slotDisplayLabel(rawCode) || `Slot ${i + 1}`);
    pushProductRows(rawCode, label, filteredPlanDays);
  }

  sortSlotIds([...productsBySlot.keys()]).forEach((slotId) => {
    if (seenSlots.has(slotId)) return;
    pushProductRows(slotId, slotDisplayLabel(slotId), filteredPlanDays);
  });

  return rows;
}
